import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DNACopyNumberCalculator = () => {
  const [formData, setFormData] = useState({
    sampleVolume: '',
    concentration: '',
    genomeSize: '',
    targetSize: '',
    sampleType: 'dsDNA',
    units: 'ng/ul'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateCopyNumber = () => {
    const { sampleVolume, concentration, genomeSize, targetSize, sampleType, units } = formData;

    if (!sampleVolume || !concentration || !genomeSize || !targetSize) {
      setError('Please fill in all required fields');
      return;
    }

    const vol = parseFloat(sampleVolume);
    const conc = parseFloat(concentration);
    const genome = parseFloat(genomeSize);
    const target = parseFloat(targetSize);

    if (isNaN(vol) || isNaN(conc) || isNaN(genome) || isNaN(target) || 
        vol <= 0 || conc <= 0 || genome <= 0 || target <= 0) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    const avogadroNumber = 6.02214076e23;
    const dsDNAWeight = 660;
    const ssDNAWeight = 330;

    let totalMass;
    if (units === 'ng/ul') {
      totalMass = vol * conc * 1e-9;
    } else {
      totalMass = vol * conc * 1e-6;
    }

    let molecularWeight;
    if (sampleType === 'dsDNA') {
      molecularWeight = genome * dsDNAWeight;
    } else {
      molecularWeight = genome * ssDNAWeight;
    }

    const molecules = (totalMass / molecularWeight) * avogadroNumber;
    const copiesPerCell = (target / genome) * molecules;

    const resultsData = {
      sampleVolume: `${vol} μL`,
      concentration: `${conc} ${units}`,
      genomeSize: `${genome} bp`,
      targetSize: `${target} bp`,
      sampleType,
      totalMass: (totalMass * (units === 'ng/ul' ? 1e9 : 1e6)).toFixed(2) + (units === 'ng/ul' ? ' ng' : ' μg'),
      molecularWeight: (molecularWeight / 1e6).toFixed(2) + ' kDa',
      molecules: molecules.toExponential(2),
      copiesPerCell: copiesPerCell.toExponential(2)
    };

    const generateChartData = () => {
      const data = {
        labels: ['Sample Volume (μL)', 'Concentration', 'Genome Size (bp)', 'Target Size (bp)'],
        datasets: [{
          label: 'Input Values',
          data: [
            vol,
            conc,
            genome,
            target
          ],
          backgroundColor: [
            'rgba(75, 192, 192, 0.2)',
            'rgba(255, 99, 132, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(255, 205, 86, 0.2)'
          ],
          borderColor: [
            'rgb(75, 192, 192)',
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(255, 205, 86)'
          ],
          borderWidth: 1
        }]
      };

      return data;
    };

    setResults(resultsData);
    setChartData(generateChartData());
  };

  return (
    <div>
      <Helmet>
        {/* Basic Page Meta */}
        <title>DNA Copy Number Calculator | Genetics Tool</title>
        <meta name="description" content="Calculate DNA copy numbers based on sample volume, concentration, and target size with precision." />
        <meta name="keywords" content="DNA calculator, genetics, molecular biology, copy number, DNA analysis" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourdomain.com/dna-copy-number-calculator" />
        <meta property="og:title" content="DNA Copy Number Calculator" />
        <meta property="og:description" content="Calculate DNA copy numbers based on sample volume, concentration, and target size with precision." />
        <meta property="og:image" content="https://yourdomain.com/preview.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourdomain.com/dna-copy-number-calculator" />
        <meta property="twitter:title" content="DNA Copy Number Calculator" />
        <meta property="twitter:description" content="Calculate DNA copy numbers based on sample volume, concentration, and target size with precision." />
        <meta property="twitter:image" content="https://yourdomain.com/preview.jpg" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 mb-8">
            <div className="text-4xl">🧬</div>
            <h1 className="text-3xl font-bold text-gray-800">DNA Copy Number Calculator</h1>
            <p className="text-gray-600">
              Calculate DNA copy numbers based on sample volume, concentration, and target size.
              <span className="block mt-1">Determine molecules count and copies per cell with precision</span>
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/80 backdrop-blur-lg rounded-xl shadow-lg p-6 md:p-8"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="sampleVolume" className="block text-sm font-medium text-gray-700 mb-2">
                  Sample Volume (μL)
                </label>
                <input
                  type="number"
                  id="sampleVolume"
                  name="sampleVolume"
                  value={formData.sampleVolume}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter sample volume in μL"
                />
              </div>

              <div>
                <label htmlFor="concentration" className="block text-sm font-medium text-gray-700 mb-2">
                  Concentration
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    id="concentration"
                    name="concentration"
                    value={formData.concentration}
                    onChange={handleInputChange}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Enter concentration"
                  />
                  <select
                    id="units"
                    name="units"
                    value={formData.units}
                    onChange={handleInputChange}
                    className="w-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="ng/ul">ng/μL</option>
                    <option value="ug/ul">μg/μL</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="genomeSize" className="block text-sm font-medium text-gray-700 mb-2">
                  Genome Size (bp)
                </label>
                <input
                  type="number"
                  id="genomeSize"
                  name="genomeSize"
                  value={formData.genomeSize}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter genome size in base pairs"
                />
              </div>

              <div>
                <label htmlFor="targetSize" className="block text-sm font-medium text-gray-700 mb-2">
                  Target Size (bp)
                </label>
                <input
                  type="number"
                  id="targetSize"
                  name="targetSize"
                  value={formData.targetSize}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter target size in base pairs"
                />
              </div>

              <div>
                <label htmlFor="sampleType" className="block text-sm font-medium text-gray-700 mb-2">
                  Sample Type
                </label>
                <select
                  id="sampleType"
                  name="sampleType"
                  value={formData.sampleType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="dsDNA">Double-stranded DNA</option>
                  <option value="ssDNA">Single-stranded DNA</option>
                </select>
              </div>

              <button
                onClick={calculateCopyNumber}
                className="w-full py-3 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors duration-200"
              >
                Calculate Copy Number
              </button>

              {error && (
                <div className="text-red-500 text-center mt-4">
                  {error}
                </div>
              )}

              {results && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Calculation Results</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Sample Volume</h3>
                      <p className="text-lg font-semibold">{results.sampleVolume}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Concentration</h3>
                      <p className="text-lg font-semibold">{results.concentration}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Genome Size</h3>
                      <p className="text-lg font-semibold">{results.genomeSize}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Target Size</h3>
                      <p className="text-lg font-semibold">{results.targetSize}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Total Mass</h3>
                      <p className="text-lg font-semibold">{results.totalMass}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Molecular Weight</h3>
                      <p className="text-lg font-semibold">{results.molecularWeight}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Number of Molecules</h3>
                      <p className="text-lg font-semibold">{results.molecules}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Copies per Cell</h3>
                      <p className="text-lg font-semibold">{results.copiesPerCell}</p>
                    </div>
                  </div>

                  {chartData && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Input Parameters</h3>
                        <Bar
                          data={chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: 'Values'
                                }
                              },
                              x: {
                                title: {
                                  display: true,
                                  text: 'Parameters'
                                }
                              }
                            },
                            plugins: {
                              title: {
                                display: true,
                                text: 'Input Parameters'
                              }
                            }
                          }}
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Result Distribution</h3>
                        <Pie
                          data={{
                            labels: ['Molecules', 'Copies per Cell'],
                            datasets: [{
                              data: [parseFloat(results.molecules), parseFloat(results.copiesPerCell)],
                              backgroundColor: [
                                'rgba(75, 192, 192, 0.7)',
                                'rgba(255, 99, 132, 0.7)'
                              ],
                              borderColor: [
                                'rgb(75, 192, 192)',
                                'rgb(255, 99, 132)'
                              ],
                              borderWidth: 1
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              title: {
                                display: true,
                                text: 'Result Distribution'
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          <div className="calculator-section mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              How to Use DNA Copy Number Calculator <span className="ml-2 text-2xl">📋</span>
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <p className="text-gray-700 mb-6">
                Follow these steps to use the DNA Copy Number Calculator effectively:
              </p>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Sample Information</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Enter your sample volume in μL</li>
                    <li>Select concentration units (ng/μL or μg/μL)</li>
                    <li>Enter the concentration value</li>
                    <li>Select your sample type (dsDNA or ssDNA)</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Genetic Information</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Enter the genome size in base pairs</li>
                    <li>Enter the target size in base pairs</li>
                    <li>Ensure values are accurate for your sample</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Calculate and Analyze</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Click "Calculate Copy Number" button</li>
                    <li>Review calculated values including total mass and molecular weight</li>
                    <li>Check number of molecules and copies per cell</li>
                    <li>Use the chart to visualize input parameters</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Important Considerations</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Ensure accurate measurements for best results</li>
                    <li>Consider sample purity and quality</li>
                    <li>Double-check genome and target sizes</li>
                    <li>Use appropriate concentration units</li>
                    <li>Compare results with expected values</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="calculator-section mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              Understanding DNA Copy Numbers <span className="ml-2 text-2xl">📊</span>
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <p className="text-gray-700 mb-6">
                DNA copy numbers are crucial for understanding genetic content and expression.
                This calculator helps determine:
              </p>
              <ul className="list-none pl-8 space-y-4">
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                  <span className="font-medium">Total Mass:</span> Total DNA mass in your sample
                </li>
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                  <span className="font-medium">Molecular Weight:</span> Weight of the DNA molecule
                </li>
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                  <span className="font-medium">Number of Molecules:</span> Total DNA molecules in your sample
                </li>
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                  <span className="font-medium">Copies per Cell:</span> Number of target copies per cell
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DNACopyNumberCalculator;